/*
 * The StepBudgetExpenseToMe component is the step in reseller SO flow enabling expense-to-me budget configuration.
 * 
 * This takes the following props:
 *   auth                     - authentication object
 *   history                  - for navigation to non-SO flow.  TODO: refactor when moving to react router 6, consider removing functionality
 *   customer                 - customer for which this SO is being created
 *   project                  - project for which this SO is being created
 *   resellerAddressPresent   - indicate whether reseller address is present
 *   assignedStep
 *   activeStep
 *   handleBack
 *   handleNext
 */

import React from 'react';
import graphql from 'babel-plugin-relay/macro';
import { QueryRenderer } from 'react-relay';
import PropTypes from 'prop-types';
import AbEnvironment from '../auth/AbEnvironment';

import { styled } from '@mui/material/styles';
import withStyles from '@mui/styles/withStyles';
import { Accordion, AccordionDetails, AccordionSummary, Button, Grid, 
         Paper, Switch, TableContainer, Table, TableBody, TableCell, TableHead, TableRow, Tooltip, Typography } from '@mui/material';
import { tableCellClasses } from '@mui/material/TableCell';
import AppBindStyles from '../../AppBindStyles';

import SubscriptionTableRow from './SubscriptionTableRow';
import Add from '@mui/icons-material/Add';
import HelpOutline from '@mui/icons-material/HelpOutline';

import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import FundingSourceTile from '../paymentsources/FundingSourceTile';
import PaymentFormII from '../paymentsources/PaymentFormII';
import ACHFormII from '../paymentsources/ACHFormII';
import PaymentSourceDialog from '../ui/PaymentSourceDialog';
import LoadingSpinner from '../ui/LoadingSpinner';
import SavingSpinner from '../ui/SavingSpinner';
import ServerError from '../ui/ServerError';
import CurrencyTextField from '../ui/CurrencyTextField';

const abEnvironment = new AbEnvironment();
const StepBudgetExpenseToMeQuery = graphql`
query StepBudgetExpenseToMeQuery($accountId: UUID!, $projectId: UUID) {
  accountTByAccountId(accountId: $accountId) {
    accountId
    accountNum
    accountName
    contactLastName
    contactFirstName
    contactEmail
    contactPhone
    accountTByParentId {
      accountStatus
      autoTopupActive
      autoTopupAmount
      autoTopupThreshhold
      initialBalanceAmount
      accountId
      accountName
      description
      contactFirstName
      contactLastName
      contactEmail
      contactPhone
      address1,
      address2,
      city
      state
      country
      postalCode
      dwollaCid
      stripeCid
      achActive
      projectTsByResellerAccountId(condition: {projectId: $projectId}) {
        totalCount
        nodes {
          projectId
          projectName
          preApproveSubs
          rPreApproveSubs
          budgetApprovedCount
          walletTByResellerWalletId {
            walletId
            initialBalanceAmount
            autoTopupActive
            autoTopupAmount
            rAutoTopupAmount
            rAutoTopupThreshold
            autoTopupThreshold
            rMaxMonthlyBudget
            maxMonthlyBudget
          }
        }
      }
      paymentSourceTsByAccountId(orderBy: PS_ORDER_ASC) {
        totalCount
        nodes {
          psType
          ccBrand
          ccLast4
          ccExpy
          fiName
          achAccountNum
          firstName
          lastName
          psOrder
        }
      }
    }
    customerAppTsByCustomerAccountId(condition: {projectId: $projectId}) {
      nodes {
        customerAppId
        appId
        customerAccountId
        accountTByCustomerAccountId {
          accountName
          contactFirstName
          contactLastName
          contactEmail
          accountType
          accountTByParentId {
            accountName
            contactFirstName
            contactLastName
            contactEmail
          }
          userTsByAccountId {
            nodes {
              accountLinkTsByUserId {
                totalCount
              }
            }
          }
          paymentSourceTsByAccountId {
            totalCount
          }
        }
        appStatus
        bindCardTByBcId {
          bcStatus
        }
        appSkuPrice
        appMarkupAbsolute
        appMarkupPercentage
        paymentOrder
        rAppMarkupPercentage
        rPaymentOrder
        rPassThroughFees
        passThroughFees
        bcId
        sharedEmail
        showSubscription
        passThroughFees
        appTByAppId {
          appName
          appSku
          appUrl
          appLogo
          appType
          description
          signupUrl
        }
      }
    }
  }
}
`;

const styleSheet = theme => ({
  root: {
    flexGrow: 1,
  },
  paper: {
    padding: theme.spacing(3),
    textAlign: 'left',
    // maxWidth: 980
  },
  percentageTextField: {
    marginTop: -4,
    marginLeft: 16,
    width: 60,
    minWidth: 60,
    textAlign: 'right',
    paddingLeft: 0
  },
  radio: {
    marginTop: -10,
    marginLeft: 16,
  },
  info: {
    marginTop: -12,
    marginBottom: 8
  },
  newAppRow: {
    // backgroundColor: yellow[100],
    borderLeft: 'solid',
    borderLeftWidth: 3,
    borderLeftColor: '#81C784;',
  },
  termsChangedRow: {
    borderLeft: 'dashed',
    borderLeftWidth: 3,
    borderLeftColor: '#FFBF00;'
  },
  unchangedRow: {
    marginLeft: 3
  },
  existingAppRow: {
    marginLeft: 6
  },
  terms: {
    maxWidth: 170,
    minWidth: 170
  }
});

const StyledTable = styled(Table) (({ theme }) => ({
  borderWidth: 1,
  borderStyle: 'solid',
  //borderColor: theme.palette.colors.secondary[200],
  borderColor: theme.palette.divider,
  borderTopLeftRadius: theme.spacing(1),
  borderTopRightRadius: theme.spacing(1),
}));

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    // backgroundColor: theme.palette.common.black,
    // color: theme.palette.common.white,
    // backgroundColor: theme.palette.colors.secondary[200],
    backgroundColor: theme.palette.divider,
    paddingTop: theme.spacing(1.5),
    paddingBottom: theme.spacing(1.5),
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 14,
    paddingTop: theme.spacing(1),
    paddingBottom: theme.spacing(1),
  },
  paddingLeft: theme.spacing(2),
  paddingRight: theme.spacing(2),
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  /*
  '&:nth-of-type(odd)': {
    backgroundColor: theme.palette.action.hover,
  },
  */
  height: 50, // Make the row heights consistent.  Smaller than this could be better, but then padding needs to be adjusted.
  // hide last border
  '&:last-child td, &:last-child th': {
    border: 0,
  },
}));

class StepBudgetExpenseToMe extends React.Component {
  
  state = {
    savedAutoTopupActive: null,
    autoTopupActive: null,
    autoTopupAmount: null,
    autoTopupThreshold: null,

    showSpinner: false,
    showACHSpinner: false,
    ccOpen: false,
    achOpen: false,

    forceFetch: 1
  }

  isPaymentSourceComplete(resellerAccount) {
    //suspended or pending accounts cannot create funding sources.  Let them skip.
    if  (resellerAccount.accountStatus === 1 || resellerAccount.accountStatus === 2) {
      return true;
    }

    if (resellerAccount.paymentSourceTsByAccountId.totalCount > 0) {
      return true;
    }

    return false;
  }

  isInformationComplete(resellerAccount, theWallet) {
    if (this.isPaymentSourceComplete(resellerAccount)) {
      if (this.isSaveBudgetDisabled(resellerAccount, theWallet)) {
        return false;
      } else {
        return true;
      }
    } else {
      return false;
    }
  }

  isBackDisabled() {
    if (this.props.activeStep === 0)
      return true;

    return false;
  }

  isNextDisabled(resellerAccount, theWallet) {
    if (this.props.activeStep === this.props.assignedStep) {
      if (!this.isInformationComplete(resellerAccount, theWallet)) {
        return true;
      } 
    }
    return false;
  }

  getValueForRender(state_member, prop_member, theDefault)
  {
    if (state_member != null)
      return state_member;

    if (prop_member != null)
      return prop_member;

    return theDefault;
  }

  openCCDialog = (event) => {
    //we have to tell payment form where to come back to 
    this.saveReturnInfo();

    this.setState({ccOpen: true, showSpinner: true});
  }

  handleCCClose = () => {
    localStorage.removeItem('paymentReturn');
    let newForceFetch = this.state.forceFetch;
    newForceFetch++;
    this.setState({ccOpen: false, showSpinner: false, forceFetch: newForceFetch});
  };
    
  openACHDialog = (event) => {
    //we have to tell payment form where to come back to 
    this.saveReturnInfo();

    this.setState({achOpen: true, showACHSpinner: true});
  }

  handleACHClose = () => {
    localStorage.removeItem('paymentReturn');
    localStorage.removeItem('plaid');

    let newForceFetch = this.state.forceFetch;
    newForceFetch++;
    this.setState({achOpen: false, showACHSpinner: false, forceFetch: newForceFetch});
  };

  saveReturnInfo = () => {
    let customer = this.props.customer;
    localStorage.setItem('soCustomerParms', JSON.stringify(customer));

    let assignedStep = this.props.assignedStep;
    //here is a little interesting.  The reseller address step is the only thing that could disappear coming back.  So, 
    //we need to know if it was there before to account for one less step
    if (!this.props.resellerAddressPresent) {
      assignedStep = assignedStep - 1;
    }
    let url = '/subscriptionorder?step=' + assignedStep;
    localStorage.setItem('paymentReturn', url);
    localStorage.removeItem('plaid');
  }

  toggleAutoTopup(ata, autoTopupAmount, autoTopupThreshold) {
    const { getTopupTargetMinimum, getTopupTargetDefault, getTopupTargetLimit, getTopupThresholdMinimum, getTopupThresholdDefault, getTopupThresholdLimit } = this.props.auth;

    if (ata === 1) {
      let theAmount = autoTopupAmount;
      if (theAmount == null || theAmount < getTopupTargetMinimum() || theAmount > getTopupTargetLimit()) {
        theAmount = getTopupTargetDefault();
      }

      let theThreshold = autoTopupThreshold;
      if (theThreshold == null || theThreshold < getTopupThresholdMinimum() || theThreshold > getTopupThresholdLimit()) {
        theThreshold = getTopupThresholdDefault();
      }

      this.setState({ autoTopupActive: 1, autoTopupAmount: theAmount, autoTopupThreshold: theThreshold});
    } else {
      this.setState({ autoTopupActive: 0, autoTopupAmount: 0, autoTopupThreshold: 0});
    }
  }

  showSpinner() {
    return this.state.showSpinner;
  }

  showACHSpinner() {
    return this.state.showACHSpinner;
  }

  setStripeCidOnUInfo = (stripeCid) => {
    //this is a no op here. The page is reloaded after card is entered and that refetches.
  }
  setDwollaCidOnUInfo = (dwollaCid) => {
    //this is a no op here. The page is reloaded after card is entered and that refetches.
  }

  openACH = () => {
    this.openACHDialog();
    this.setState({showACHSpinner: true}); 
  }

  openCC = () => {
    this.openCCDialog();
    this.setState({showSpinner: true}); 
  }

  getAutoTopupAmountErrorMessage(theAccount, theWallet) {
    const { getTopupTargetDefault, getTopupThresholdDefault } = this.props.auth;

    let errMesg = null;

    let amountVal;
    let thresholdVal;
    if (theWallet != null) {
      amountVal    = Number(this.getValueForRender(this.state.autoTopupAmount, theWallet.autoTopupAmount, getTopupTargetDefault())); 
      thresholdVal = Number(this.getValueForRender(this.state.autoTopupThreshold, theWallet.autoTopupThreshold, getTopupThresholdDefault())); 
    } else {
      amountVal    = Number(this.getValueForRender(this.state.autoTopupAmount, theAccount.autoTopupAmount, getTopupTargetDefault())); 
      thresholdVal = Number(this.getValueForRender(this.state.autoTopupThreshold, theAccount.autoTopupThreshhold, getTopupThresholdDefault())); 
    }

    if (amountVal < thresholdVal) {
      errMesg = 'Cannot be less than falls below value';
    }
    return errMesg;
  }

  isAutoTopupAmountError(theAccount, theWallet) {
    if (this.getAutoTopupAmountErrorMessage(theAccount, theWallet) == null) {
      return false;
    }
    return true;
  }

  isAutoTopupAmountValid(theAccount, theWallet) {
    const { getTopupTargetMinimum, getTopupTargetDefault, getTopupTargetLimit, getTopupThresholdDefault  } = this.props.auth;

    let val;
    let autoTopupThreshold;

    if (theWallet != null) {
      val = Number(this.getValueForRender(this.state.autoTopupAmount, theWallet.autoTopupAmount, getTopupTargetDefault())); 
      autoTopupThreshold = Number(this.getValueForRender(this.state.autoTopupThreshold, theWallet.autoTopupThreshold, getTopupThresholdDefault())); 
    } else {
      val = Number(this.getValueForRender(this.state.autoTopupAmount, theAccount.autoTopupAmount, getTopupTargetDefault())); 
      autoTopupThreshold = Number(this.getValueForRender(this.state.autoTopupThreshold, theAccount.autoTopupThreshhold, getTopupThresholdDefault())); 
    }

    // Don't allow the amout to be less than the topup target limit
    if (val <  getTopupTargetMinimum() || val > getTopupTargetLimit()) {
      return false;
    }

    // Don't allow the amout to be less than the threshold
    if (val < autoTopupThreshold) {
      return false;
    }

    return true;
  }

  // Determine if the button in the budget paper should be disabled
  isSaveBudgetDisabled(theAccount, theWallet) {
    //this validated only if something is in error (need to have next enabled otherwise so they can move on)

    let theSavedAutoTopupActive;
    if (theWallet != null) {
      theSavedAutoTopupActive = theWallet.autoTopupActive;
    } else {
      theSavedAutoTopupActive = theAccount.autoTopupActive;
    }
    if (this.state.savedAutoTopupActive != null) {
      theSavedAutoTopupActive = this.state.savedAutoTopupActive;
    }

    // - only validate the auto topup amount and threshold if auto topup is active.
    let autoTopupIsActive = theSavedAutoTopupActive;
    if (this.state.autoTopupActive === 1) {
      autoTopupIsActive = this.state.autoTopupActive;
    }

    if (autoTopupIsActive === 1) {
      if (!this.isAutoTopupAmountValid(theAccount, theWallet)) {
        return true;
      }
  
      if (this.state.autoTopupThreshold != null && this.state.autoTopupThreshold < 20) {
        return true;
      }
    }
    
    return false;
  }

  render() {
    const { logError, getTopupThresholdMinimum, getTopupThresholdDefault, getTopupThresholdLimit, getTopupTargetMinimum, getTopupTargetDefault, getTopupTargetLimit } = this.props.auth;
    // const { logError, getTopupThresholdLimit, getTopupTargetLimit  } = this.props.auth;
    const classes = this.props.classes;

    let displayOnly = false;
    if (this.props.activeStep > this.props.assignedStep) {
      displayOnly = true;
    }
    
    let acctId = this.props.customer.accountId;

    // Was a project passed in?  If so, the subscription is for a project.
    // If not, legacy case.
    let aProject = this.props.project;
    let projectId = null;
    if (aProject != null && aProject.projectId != null) {
      projectId = aProject.projectId;
    }

    var fundingConnected = false;

    return (
      <QueryRenderer
        environment={abEnvironment.getEnvironment()}
        query={StepBudgetExpenseToMeQuery}
        variables={{accountId: acctId, projectId: projectId, forceFetch: this.state.forceFetch}}
        render={({error, props})=> {
          if (error) {
            logError(error.message);
            return (
              <ServerError/>
            );
          } else if (props) {

            let theAccount          = props.accountTByAccountId;
            let resellerAccount     = theAccount.accountTByParentId;
            let resellerAccountName = resellerAccount.accountName;

            //
            // If projects are used, values will be on the project and wallet.
            // If projects are not used, values will be on the account.
            // - so set up variables that will be used on the UI here, and set
            //   them from either the project/wallet or the account.
            // 

            // Project if relevant
            let theProject = null;
            let theWallet = null;
            if (resellerAccount.projectTsByResellerAccountId.totalCount > 0) {
              theProject = resellerAccount.projectTsByResellerAccountId.nodes[0];
              theWallet  = theProject.walletTByResellerWalletId;
            }

            let autoTopupActive    = resellerAccount.autoTopupActive;
            let autoTopupThreshold = resellerAccount.autoTopupThreshhold;
            let autoTopupAmount    = resellerAccount.autoTopupAmount;

            if (theWallet != null) {
              autoTopupActive    = theWallet.autoTopupActive;
              autoTopupThreshold = theWallet.autoTopupThreshold;
              autoTopupAmount    = theWallet.autoTopupAmount;
            }

            if (this.state.autoTopupActive != null) {
              autoTopupActive = this.state.autoTopupActive;
            }
 
            // Limit functionality is used to prevent funding when an account is suspended or pending
            let limitFunctionality = true;
            if  (resellerAccount.accountStatus === 1 || resellerAccount.accountStatus === 2) {
              limitFunctionality = true;
            } else {
              limitFunctionality = false;
            }

            let uInfo = {
              accountId:   resellerAccount.accountId,
              accountName: resellerAccountName,
              description: resellerAccount.description,
              firstName:   resellerAccount.contactFirstName,
              lastName:    resellerAccount.contactLastName,
              email:       resellerAccount.contactEmail,
              phone:       resellerAccount.contactPhone,
              address1:    resellerAccount.address1,
              address2:    resellerAccount.address2,
              city:        resellerAccount.city,
              state:       resellerAccount.state,
              country:     resellerAccount.country,
              postalCode:  resellerAccount.postalCode,
              dwollaCid:   resellerAccount.dwollaCid,
              stripeCid:   resellerAccount.stripeCid,
              achActive:   resellerAccount.achActive
            };
            // Is there already a payment source?
            let achAdded = false;
            let ccAdded = false;
            let ccPaymentSource = null;
            let achPaymentSource = null
            for (let i = 0; i < resellerAccount.paymentSourceTsByAccountId.nodes.length; i++) {
              let ps =  resellerAccount.paymentSourceTsByAccountId.nodes[i];
              if (ps.psType === 0) {
                ccAdded = true;
                ccPaymentSource = resellerAccount.paymentSourceTsByAccountId.nodes[i];
              } else if (ps.psType === 1) {
                achAdded = true;
                achPaymentSource = resellerAccount.paymentSourceTsByAccountId.nodes[i];
              }
            }

            if (resellerAccount != null && resellerAccount.paymentSourceTsByAccountId.totalCount > 0) {
              fundingConnected = true;
            }

            let expenseToMeSubscriptions = [];
            let existingMeSubCount = 0;
            let pendingMeSubCount = 0;

            for (let i = 0; i < props.accountTByAccountId.customerAppTsByCustomerAccountId.nodes.length; i++) {
              let sub = props.accountTByAccountId.customerAppTsByCustomerAccountId.nodes[i];
              //exclude cancelled subs
              if (sub.appStatus !== 2) {
                if (sub.rPaymentOrder !== null) {
                  if (sub.rPaymentOrder !== 2) {
                  } else {
                    expenseToMeSubscriptions.push(sub);
                    if (sub.bindCardTByBcId.bcStatus === 0) {
                      existingMeSubCount++;
                    } else {
                      pendingMeSubCount++;
                    }
                  }
  
                } else {
                  if (sub.paymentOrder !== 2) {
                  } else {
                    expenseToMeSubscriptions.push(sub);
                    if (sub.bindCardTByBcId.bcStatus === 0) {
                      existingMeSubCount++;
                    } else {
                      pendingMeSubCount++;
                    }
                  }
                }
              }
            }

            // sort expense to me list
            if (expenseToMeSubscriptions.length > 0) {
              expenseToMeSubscriptions.sort(function(a, b) {
                if(a.appTByAppId.appName < b.appTByAppId.appName) { return -1; }
                if(a.appTByAppId.appName > b.appTByAppId.appName) { return 1; }
                return 0;
              });
            }

            let expenseToMeAccordionTitle0 = 'Set up an expense wallet for ';
            let expenseToMeAccordionTitle2 = '';

            if (existingMeSubCount === 1) {
              expenseToMeAccordionTitle2 = existingMeSubCount + ' existing subscription';
            } else if (existingMeSubCount > 1) {
              expenseToMeAccordionTitle2 = existingMeSubCount + ' existing subscriptions';
            }

            if (pendingMeSubCount === 1) {
              if (expenseToMeAccordionTitle2 === '') {
                expenseToMeAccordionTitle2 = pendingMeSubCount + ' new subscription';
              } else {
                expenseToMeAccordionTitle2 = expenseToMeAccordionTitle2 + ' and ' + pendingMeSubCount + ' new subscription';
              }
            } else if (pendingMeSubCount > 1) {
              if (expenseToMeAccordionTitle2 === '') {
                expenseToMeAccordionTitle2 = pendingMeSubCount + ' new subscriptions';
              } else {
                expenseToMeAccordionTitle2 = expenseToMeAccordionTitle2 + ' and ' + pendingMeSubCount + ' new subscriptions';
              }
            }

            if (!displayOnly) {
              return (
                <Grid container spacing={2}>
                  {expenseToMeSubscriptions.length > 0 &&         
                  <Grid item xs={12}>      
                    <Paper className={classes.paper}>
                      <Grid container spacing={2} direction="row" justifyContent="space-between" >
                        <Grid item xs={12}>
                          <Typography variant="h6">
                            Wallet for subscriptions expensed to me
                          </Typography>
                        </Grid>
                        
                        <Grid item xs={12}>
                          <Accordion>
                            <AccordionSummary
                              expandIcon={<ExpandMoreIcon />}
                              aria-controls="panel1b-content"
                              id="panel1b-header"
                            >
                              <Typography>
                                {expenseToMeAccordionTitle0}&nbsp;
                                <span style={{color: "blue", textDecoration: "underline"}}>{expenseToMeAccordionTitle2}</span>
                              </Typography>
                            </AccordionSummary>
                            <AccordionDetails>
                              {expenseToMeSubscriptions.length > 0 &&
                              <TableContainer>
                                <StyledTable>
                                  <TableHead>
                                    <TableRow>
                                      <StyledTableCell sx={{ borderTopLeftRadius: 8}}/>
                                      <StyledTableCell>Subscription</StyledTableCell>
                                      <StyledTableCell sx={{ borderTopRightRadius: 8}}/>
                                    </TableRow>
                                  </TableHead>
                                  <TableBody>
                                    {expenseToMeSubscriptions.map(n => {
                                      return (
                                        <SubscriptionTableRow 
                                          key={n.customerAppId}
                                          subscription={n}
                                          styledTableRow={StyledTableRow}
                                          styledTableCell={StyledTableCell}
                                          editable={false}
                                          showModel={false}
                                          showTerms={false}
                                        />
                                      );
                                    })}
                                  </TableBody>
                                </StyledTable>
                              </TableContainer>}
                            </AccordionDetails>
                          </Accordion>
                        </Grid>
                        
                        {fundingConnected &&
                        <React.Fragment>
                          <Grid item xs={12}>
                            <Grid container justifyContent="space-between" alignItems="center" spacing={0}>
                              <Grid item>
                                <Grid container justifyContent="flex-start" alignItems="center" spacing={0}>
                                  <Grid item>
                                    <Typography variant="body1" style={AppBindStyles.boldLabel}>Automatically add funds</Typography>
                                  </Grid>
                                  <Grid item>
                                    <Tooltip placement='bottom' title="When your balance goes below the limit you specify, you'll be billed the amount needed to bring your balance back up to your desired funding level.">
                                      <HelpOutline fontSize="small" style={{verticalAlign: "-7%", marginLeft: '4px'}}/>
                                    </Tooltip>
                                  </Grid>
                                </Grid>
                              </Grid>
                              <Grid item>
                                <Switch checked={autoTopupActive === 1} 
                                        onChange={(e, checked) => {
                                          if (checked) {
                                            this.toggleAutoTopup(1, autoTopupAmount, autoTopupThreshold);
                                          } else {
                                            this.toggleAutoTopup(0, autoTopupAmount, autoTopupThreshold);
                                          }
                                        }}
                                        color="primary"
                                        disabled={limitFunctionality === true}
                                />
                              </Grid>
                            </Grid>
                          </Grid>

                          <Grid item xs={12}>
                            <Grid container justifyContent="space-between" alignItems="center" spacing={0}>
                              <Grid item>
                                {autoTopupActive === 0 &&
                                <Typography variant="body1">When balance falls below</Typography>}
                                {autoTopupActive !== 0 &&
                                <Typography variant="body1" style={AppBindStyles.boldLabel}>When balance falls below</Typography>}
                              </Grid>
                              <Grid item>
                                <CurrencyTextField id="fallsBelowPending" className={classes.currencyField3}
                                  value={this.getValueForRender(this.state.autoTopupThreshold, autoTopupThreshold, getTopupThresholdDefault())}
                                  showErrMsg={true}
                                  allowNegative={false}
                                  minValue={getTopupThresholdMinimum()}
                                  maxValue={getTopupThresholdLimit()}
                                  minEnterValue={0}
                                  maxEnterValue={999999}
                                  onChange={(e) => this.setState({ autoTopupThreshold: e.target.value })}
                                  disabled={autoTopupActive === 0}
                                  textAlign="right"
                                />
                              </Grid>
                            </Grid>
                          </Grid>
                          <Grid item xs={12}>
                            <Grid container justifyContent="space-between" alignItems="center" spacing={0}>
                              <Grid item>
                                {autoTopupActive === 0 &&
                                <Typography variant="body1">Fund balance to</Typography>}
                                {autoTopupActive !== 0 &&
                                <Typography variant="body1" style={AppBindStyles.boldLabel}>Fund balance to</Typography>}
                              </Grid>
                              <Grid item>
                                <CurrencyTextField id="autoTopupAmountPending" className={classes.currencyField3}
                                  value={this.getValueForRender(this.state.autoTopupAmount, autoTopupAmount, getTopupTargetDefault())}
                                  showErrMsg={true}
                                  error={this.isAutoTopupAmountError(resellerAccount, theWallet)}
                                  errorMsg={this.getAutoTopupAmountErrorMessage(resellerAccount, theWallet)}
                                  allowNegative={false}
                                  minValue={getTopupTargetMinimum()}
                                  maxValue={getTopupTargetLimit()}
                                  minEnterValue={0}
                                  maxEnterValue={99999999}
                                  onChange={(e) => this.setState({ autoTopupAmount: e.target.value })}
                                  disabled={autoTopupActive === 0}
                                  textAlign="right"
                                />
                              </Grid>
                            </Grid>
                          </Grid>
                          <Grid item xs={12}>
                            {(resellerAccount.accountStatus === 2) &&
                            <Paper elevation={1} style={AppBindStyles.tipPaper}>
                              <Grid container spacing={1} direction="row" justifyContent="flex-start">
                                <Grid item xs={12}>
                                  <div style={{display:"flex"}}>
                                    <Typography variant="body1">
                                      <strong>Tip.</strong>&nbsp;&nbsp;We won't charge you until your account is verified and approved.
                                    </Typography>
                                  </div>
                                </Grid>      
                              </Grid>
                            </Paper>}
                            {(resellerAccount.accountStatus === 0) &&
                            <Paper elevation={1} style={AppBindStyles.tipPaper}>
                              <Grid container spacing={1} direction="row" justifyContent="flex-start">
                                <Grid item xs={12}>
                                  <div style={{display:"flex"}}>
                                    <Typography variant="body1">
                                      <strong>Tip.</strong>&nbsp;&nbsp;Unused funds are refundable to you.
                                    </Typography>
                                  </div>
                                </Grid>      
                              </Grid>
                            </Paper>}
                          </Grid>
                        </React.Fragment>}
                      </Grid>
                    </Paper>
                  </Grid>}

                  {ccAdded && false &&
                  <Grid item xs={12}>
                    <Grid container spacing={1} direction="row" justifyContent="flex-start">              
                      <Grid item style={{marginLeft: -12}} >
                        <FundingSourceTile 
                          psType={ccPaymentSource.psType}
                          ccBrand={ccPaymentSource.ccBrand}
                          ccLast4={ccPaymentSource.ccLast4}
                          ccExpy={ccPaymentSource.ccExpy}
                          fiName={ccPaymentSource.fiName}
                          achAccountNum={ccPaymentSource.achAccountNum}
                          firstName={ccPaymentSource.firstName}
                          lastName={ccPaymentSource.lastName}
                        />
                      </Grid>
                    </Grid>
                  </Grid>}
                  {achAdded && false &&
                  <Grid item xs={12}>
                    <Grid container spacing={1} direction="row" justifyContent="flex-start">              
                      <Grid item style={{marginLeft: -12}}>
                        <FundingSourceTile 
                          psType={achPaymentSource.psType}
                          ccBrand={achPaymentSource.ccBrand}
                          ccLast4={achPaymentSource.ccLast4}
                          ccExpy={achPaymentSource.ccExpy}
                          fiName={achPaymentSource.fiName}
                          achAccountNum={achPaymentSource.achAccountNum}
                          firstName={achPaymentSource.firstName}
                          lastName={achPaymentSource.lastName}
                        />
                      </Grid>
                    </Grid>
                  </Grid>}

                  {!fundingConnected && !limitFunctionality &&
                  <Grid item xs={12} style={{marginBottom: 16}}>
                    <Grid item xs={12}>                   
                      <Typography variant="body2" style={{fontWeight:600, marginBottom:16}}>
                        Connect a funding source.
                      </Typography>
                    </Grid>
                    <Grid container spacing={2} >
                      {!this.showSpinner() &&
                      <Grid item>
                        <Button color="primary" 
                                variant="outlined"
                                onClick={() => { this.openCC();}}>
                            <Add/> Add Credit Card
                        </Button>
                      </Grid>
                      }
                      {this.showSpinner() &&
                      <Grid item>
                        <Grid item>
                          <Button color="primary" 
                                  variant="outlined">
                              <SavingSpinner/>
                          </Button>
                        </Grid>
                      </Grid>
                      }

                      {!this.showACHSpinner() && uInfo.achActive &&
                      <Grid item>
                        <Button color="primary" 
                                variant="contained"
                                onClick={() => { this.openACH();}}>
                            <Add/> Add ACH
                        </Button>
                      </Grid>
                      }
                      
                      {this.showACHSpinner() && uInfo.achActive &&
                      <Grid item>
                        <Button color="primary" 
                                variant="contained">
                            <SavingSpinner/>
                        </Button>
                      </Grid>
                      }

                    </Grid>
                  </Grid>}

                  {limitFunctionality &&
                  <Grid item xs={12} style={{marginBottom: 16}}>

                    <Paper elevation={1} style={AppBindStyles.tipPaper}>
                      <Grid container spacing={1} direction="row" justifyContent="flex-start">
                        <Grid item xs={12}>
                          <div style={{display:"flex"}}>
                            <Typography variant="body1">
                              <strong>Tip.</strong>&nbsp;&nbsp;In order for you to connect a bank account or credit card, we need to know you a bit better. When you are done creating this order, we will take you to the verification step!
                            </Typography>
                          </div>
                        </Grid>      
                      </Grid>
                    </Paper>                  
                  </Grid>}

                  <PaymentSourceDialog
                      title="Add credit card"
                      open={this.state.ccOpen}
                      onClose={this.handleCCClose} >
                          <PaymentFormII pcId={uInfo.stripeCid} 
                                      dAccountId={uInfo.accountId}
                                      dAccountName={uInfo.accountName}
                                      dFirstName={uInfo.firstName}
                                      dLastName={uInfo.lastName}
                                      dEmail={uInfo.email}
                                      dPhone={uInfo.phone}
                                      dAddress1={uInfo.address1}
                                      dAddress2={uInfo.address2}
                                      dCity={uInfo.city}
                                      dState={uInfo.state}
                                      dCountry={uInfo.country}
                                      dPostalCode={uInfo.postalCode}
                                      auth={this.props.auth}
                                      porder={0}
                                      setStripeCid={this.setStripeCidOnUInfo}
                                      history={this.props.history}
                                      onClose={this.handleCCClose}
                          />
                  </PaymentSourceDialog>
                  {this.state.achOpen &&
                  <PaymentSourceDialog
                    title="Add US Bank Account (ACH)"
                    open={this.state.achOpen}
                    onClose={this.handleACHClose} >
    
                    <ACHFormII pcId={uInfo.dwollaCid} 
                            dAccountId={uInfo.accountId}
                            dAccountName={uInfo.accountName}
                            dFirstName={uInfo.firstName}
                            dLastName={uInfo.lastName}
                            dEmail={uInfo.email}
                            dPhone={uInfo.phone}
                            dAddress1={uInfo.address1}
                            dAddress2={uInfo.address2}
                            dCity={uInfo.city}
                            dState={uInfo.state}
                            dCountry={uInfo.country}
                            auth={this.props.auth}
                            porder={0}
                            setDwollaCidOnUInfo={this.setDwollaCidOnUInfo}
                            history={this.props.history}
                            onClose={this.handleACHClose}
                            />
                  </PaymentSourceDialog>}

                  {/* leave this == instead of ===.  Not 100% why as code mirrors customer authorization but was not working here when step passed in */}
                  {this.props.activeStep == this.props.assignedStep &&
                  <Grid item xs={12}>
                    <Button
                      disabled={this.isBackDisabled()}
                      color="primary" variant="outlined"
                      onClick={() => this._updateAccountOrWalletAutoTopup(resellerAccount, theWallet, "back")}
                      style={AppBindStyles.bottomButton}>
                      Back
                    </Button>
                    
                    <Button
                      color="primary" variant="contained"
                      disabled={this.isNextDisabled(resellerAccount, theWallet)}
                      onClick={() => this._updateAccountOrWalletAutoTopup(resellerAccount, theWallet, "next")}
                      style={AppBindStyles.bottomButton}>
                        Next
                    </Button>
                  </Grid>} 
                </Grid>       
              );
          
            } else {
              //display only

              let expenseToMeAccordionTitle0 = 'Expense wallet for ';
              let expenseToMeAccordionTitle2 = '';
  
              if (existingMeSubCount === 1) {
                expenseToMeAccordionTitle2 = existingMeSubCount + ' existing subscription';
              } else if (existingMeSubCount > 1) {
                expenseToMeAccordionTitle2 = existingMeSubCount + ' existing subscriptions';
              }
  
              if (pendingMeSubCount === 1) {
                if (expenseToMeAccordionTitle2 === '') {
                  expenseToMeAccordionTitle2 = pendingMeSubCount + ' new subscription';
                } else {
                  expenseToMeAccordionTitle2 = expenseToMeAccordionTitle2 + ' and ' + pendingMeSubCount + ' new subscription';
                }
              } else if (pendingMeSubCount > 1) {
                if (expenseToMeAccordionTitle2 === '') {
                  expenseToMeAccordionTitle2 = pendingMeSubCount + ' new subscriptions';
                } else {
                  expenseToMeAccordionTitle2 = expenseToMeAccordionTitle2 + ' and ' + pendingMeSubCount + ' new subscriptions';
                }
              }

              let resellerAutoTopupAmount = Number(this.getValueForRender(this.state.autoTopupAmount, autoTopupAmount, 0))
              .toLocaleString("en-US", {style: "currency", currency: "USD", minimumFractionDigits: 2}); 

              let resellerAutoTopupThreshold = Number(this.getValueForRender(this.state.autoTopupThreshold, autoTopupThreshold, 0))
              .toLocaleString("en-US", {style: "currency", currency: "USD", minimumFractionDigits: 2}); 

              return ( 
                <Grid container spacing={2} >
                  {(expenseToMeSubscriptions.length > 0) &&
                  <Grid item xs={12}>
                    <Grid container spacing={2} direction="row" justifyContent="space-between" >
                      <Grid item xs={12}>
                        <Typography variant="h6">
                          Wallet for subscriptions expensed to me
                        </Typography>
                        
                      </Grid>

                      {expenseToMeSubscriptions.length > 0 &&
                      <Grid item xs={12}>
                        <Accordion>
                          <AccordionSummary
                            expandIcon={<ExpandMoreIcon />}
                            aria-controls="panel1a-content"
                            id="panel1a-header"
                          >
                            <Typography>
                              {expenseToMeAccordionTitle0}&nbsp;
                              <span style={{color: "blue", textDecoration: "underline"}}>{expenseToMeAccordionTitle2}</span>
                            </Typography>
                          </AccordionSummary>
                          <AccordionDetails>
                            {expenseToMeSubscriptions.length > 0 &&
                            <TableContainer>
                              <StyledTable>
                                <TableHead>
                                  <TableRow>
                                    <StyledTableCell sx={{ borderTopLeftRadius: 8}}/>
                                    <StyledTableCell>Subscription</StyledTableCell>
                                    <StyledTableCell sx={{ borderTopRightRadius: 8}}/>
                                  </TableRow>
                                </TableHead>
                                <TableBody>
                                  {expenseToMeSubscriptions.map(n => {
                                    return (
                                      <SubscriptionTableRow 
                                        key={n.customerAppId}
                                        subscription={n}
                                        styledTableRow={StyledTableRow}
                                        styledTableCell={StyledTableCell}
                                        editable={false}
                                        showModel={false}
                                        showTerms={false}
                                      />
                                    );
                                  })}
                                </TableBody>
                              </StyledTable>
                            </TableContainer>}
                          </AccordionDetails>
                        </Accordion>
                      </Grid>}

                      {/*
                      <Grid item xs={12}>
                        <Grid container justifyContent="space-between" alignItems="center" spacing={0}>
                          <Grid item>
                            <Grid container justifyContent="flex-start" alignItems="center" spacing={0}>
                              <Grid item>
                                <Typography variant="body1" style={AppBindStyles.boldLabel}>Automatically add funds</Typography>
                              </Grid>
                              <Grid item>
                                <Tooltip placement='bottom' title="When your balance goes below the limit you specify, you'll be billed the amount needed to bring your balance back up to your desired funding level.">
                                  <HelpOutline fontSize="small" style={{verticalAlign: "-7%", marginLeft: '4px'}}/>
                                </Tooltip>
                              </Grid>
                            </Grid>
                          </Grid>
                          <Grid item>
                            <Switch checked={autoTopupActive === 1} 
                                    onChange={(e, checked) => {
                                      if (checked) {
                                        this.setState({ autoTopupActive: 1});
                                      } else {
                                        this.setState({ autoTopupActive: 0});
                                      }
                                    }}
                                    color="primary"
                                    disabled={true}
                            />
                          </Grid>
                        </Grid>
                      </Grid>
                      */}
                      
                      {autoTopupActive === 1 &&
                      <Grid item xs={12}>
                        <Grid container spacing={1} direction="row" justifyContent="space-between" >
                          <Grid item xs={12}>
                            <Grid container spacing={2} direction="row" justifyContent="space-between" >
                              <Grid item>
                                <Typography variant="body1" style={AppBindStyles.boldLabel}>When balance falls below</Typography>
                              </Grid>
                              <Grid item>
                                <Typography>{resellerAutoTopupThreshold}</Typography>
                              </Grid>
                            </Grid>
                          </Grid>
                          <Grid item xs={12}>
                            <Grid container spacing={2} direction="row" justifyContent="space-between" >
                              <Grid item>
                                <Typography variant="body1" style={AppBindStyles.boldLabel}>Fund balance to</Typography>
                              </Grid>
                              <Grid item>
                                <Typography>{resellerAutoTopupAmount}</Typography>
                              </Grid>
                            </Grid>
                          </Grid>
                        </Grid>
                      </Grid>}

                    </Grid>
                  </Grid>}
                </Grid>       
              ); 
            }
          }
          if (!displayOnly) {
            return <div><LoadingSpinner/></div>
          }
        }
      }
      />
    );
  }

  /* Call a backend method to update and see if a charge should happen right away */
  _updateAccountOrWalletAutoTopup(theAccount, theWallet, nextStep) {
    this.setState({ saveStatusBudget: 'saving' });
    const { payFromBalance, autoTopupActive, autoTopupAmount, autoTopupThreshold, savedAutoTopupActive } = this.state;

    const { callPartnerApi } = this.props.auth;

    // TODO: this should be altered to be more clear.  What we're saying here is that since we're in the "expense to me" step,
    //       the using account id is the same as the provisioning account id.  But this seems confusing.
    let usingAccountId = abEnvironment.getUserAttribute('a_id');
    let pAccountId = abEnvironment.getUserAttribute('a_id');

    /*
     * Make sure that there is something to do
     */
    if (payFromBalance == null && autoTopupActive == null && autoTopupAmount == null && autoTopupThreshold == null) {
      if (nextStep === "next") {
        this.props.handleNext();
      } else {
        this.props.handleBack();
      }
      return;
    }

    // What is the current state of autoTopupActive
    let theWalletId = null;
    let currentAutoTopupActive;
    if (theWallet != null) {
      currentAutoTopupActive = theWallet.autoTopupActive;
      theWalletId = theWallet.walletId;
    } else {
      currentAutoTopupActive = theAccount.autoTopupActive;
    }

    if (savedAutoTopupActive != null) {
      currentAutoTopupActive = savedAutoTopupActive;
    }

    /*
     * TODO: the API operation is not complete.  In this iteration, it assumes that there is just one wallet for the reseller, so
     *       it doesn't look at the passed walletId.  Instead, there is logic in the operation and functions to find some wallet
     *       to set and topup.  We need to change this to be a declarative action.
     * 
     *       As we used the current logged in user (reseller), the accountId and provisioningAccountId are the same, and are the reseller.
     */
    let payload = {
      operation: "saveAndProcessAutoTopup",
      params: {
        accountId:                usingAccountId,
        provisioningAccountId:    pAccountId,
        walletId:                 theWalletId,  // TODO: the topup function needs to be updated to handle this
        payFromBalance:           payFromBalance,
        autoTopupActive:          autoTopupActive,
        currentAutoTopupActive:   currentAutoTopupActive,
        autoTopupAmount:          autoTopupAmount,
        autoTopupThreshold:       autoTopupThreshold
      }
    }
    
    callPartnerApi('appbind/generic',
                   'POST',
                   payload
    ).then(response => {
      return response.json();
    })
    .then(data => {
      if (data.status === 0 || data.status === 1) {

        let theState = {
         
        };

        if (autoTopupActive != null) {
          theState.savedAutoTopupActive = autoTopupActive;
        }
        if (autoTopupAmount != null) {
          theState.savedAutoTopupAmount = autoTopupAmount;
        }
        if (autoTopupThreshold != null) {
          theState.savedAutoTopupThreshold = autoTopupThreshold;
        }
  
        this.setState(theState);
        if (nextStep === "next") {
          this.props.handleNext();
        } else {
          this.props.handleBack();
        }
      } else {
        if (nextStep === "next") {
          this.props.handleNext();
        } else {
          this.props.handleBack();
        }
      }
    })
    .catch(error => {
      //TODO not much to do here?
      if (nextStep === "next") {
        this.props.handleNext();
      } else {
        this.props.handleBack();
      }
    });
  }
}

StepBudgetExpenseToMe.propTypes = {
  assignedStep: PropTypes.number.isRequired,
  activeStep: PropTypes.number.isRequired,
  handleBack: PropTypes.func.isRequired,
  handleNext: PropTypes.func.isRequired
};

export default withStyles(styleSheet)(StepBudgetExpenseToMe);
